/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The sheet every mobile decision rises in: "Mais", the account menu, the
 * shortcut editor, the filters, the stage picker.
 *
 * It is positioned `absolute` inside the agency shell rather than `fixed` to
 * the viewport. Both look identical in a normal tab, but `fixed` resolves
 * against the wrong box the moment the shell is rendered inside an embed,
 * which is how the design prototypes are reviewed — the sheet lands halfway
 * up the page and nobody can say why.
 *
 * It covers the bottom bar rather than sitting above it. A sheet opened
 * *from* the bar that leaves the bar tappable invites a second navigation
 * behind the thing you are already reading.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Cross2Icon } from '@/shared/ui/radix-icons';
import { Text, UnstyledButton } from '@/shared/ui';

interface MobileBottomSheetProps {
  open: boolean;
  title: string;
  /** Rendered on the title row's right, before the close button. */
  action?: ReactNode;
  /** Rendered under the title, above the scrolling body. */
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
}

/**
 * A bottom sheet with a grab handle, a title row and a scrolling body.
 *
 * Escape closes it and focus moves into it on open, because the trigger is
 * usually a bar button that stays under the sheet — leaving focus there
 * would let a keyboard or switch user tab straight back out into content
 * they cannot see.
 */
export const MobileBottomSheet: React.FC<MobileBottomSheetProps> = ({
  open,
  title,
  action,
  subtitle,
  onClose,
  children,
  testId = 'mobile-bottom-sheet',
}) => {
  const { t } = useTranslation('@pages/layout');
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    sheetRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        data-testid={`${testId}-backdrop`}
        className="absolute inset-0 z-1001"
        style={{ background: 'var(--overlay-scrim)' }}
        onClick={onClose}
      />
      <div
        ref={sheetRef}
        data-testid={testId}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="absolute bottom-0 left-0 right-0 z-1003 flex flex-col outline-none"
        style={{
          maxHeight: '82%',
          background: 'var(--surface-primary)',
          borderTop: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
          boxShadow: '0 -8px 28px -12px rgba(26,26,26,0.35)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          animation: 'sheetUp 180ms ease',
        }}
      >
        <Text
          component="span"
          aria-hidden="true"
          className="self-center"
          style={{
            width: 'var(--space-10)',
            height: 'var(--space-1)',
            margin: 'var(--space-2) 0',
            borderRadius: 'var(--radius-full)',
            background: 'var(--border-subtle)',
          }}
        />

        <div
          className="flex items-center"
          style={{ gap: 'var(--space-2)', padding: '0 var(--space-4) var(--space-3)' }}
        >
          <Text
            component="span"
            data-testid={`${testId}-title`}
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 'var(--text-app-section)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--text-primary)',
            }}
          >
            {title}
          </Text>
          {action}
          <UnstyledButton
            data-testid={`${testId}-close`}
            aria-label={t('menu.close')}
            onClick={onClose}
            className="flex shrink-0 items-center justify-center"
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-full)',
              color: 'var(--text-tertiary)',
            }}
          >
            <Cross2Icon width={16} height={16} />
          </UnstyledButton>
        </div>

        {subtitle}

        <div
          className="min-h-0 flex-1 overflow-y-auto"
          style={{ padding: '0 var(--space-4) var(--space-5)' }}
        >
          {children}
        </div>
      </div>
    </>
  );
};

export default MobileBottomSheet;
